import React from "react";
import { useSelector } from "react-redux";
import { constants } from "@qgiv/core-js";
import { SystemFieldGroupPrivacyOptions } from "@qgiv/core-donor";
import { useFormikContext } from "formik";
import { getSelectedRecipient } from "@qgiv/p2p-registration";
import { selectCmsControlByIdByPage } from "../../../redux/slices/cmsSettingsSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";
import {
    selectAllRegistration,
    selectRegistrationTotals,
} from "../../../redux/slices/registrationSlice";
import { selectIsCms } from "../../../redux/slices/configSlice";
import { selectTeams } from "../../../redux/slices/teamsSlice";
import { selectAllClassifications } from "../../../redux/slices/classificationsSlice";

/**
 * @param {object} props - The component props.
 * @param {number} props.controlId
 * @returns {React.ReactElement} The rendered component.
 */
const ConnectedSystemFieldGroupPrivacyOptions = ({ controlId }) => {
    // -------------------------------------------------------------------------
    // NOTE: Currently P2P Registration is not using System Fields. This will
    //      be updated in the near future. We are using placeholder data for now.
    //      Eventually the field group and fields data will be moved to system
    //      fields and the form will be updated to use them. At that point we
    //      will update this component by removing the hardcoded fields and instead
    //      pulling them directly from the Fields table.
    // -------------------------------------------------------------------------
    const {
        ENUMS: { DefaultView, FieldType },
    } = constants;

    const { errors, handleChange, handleBlur, setFieldValue, touched, values } =
        useFormikContext();

    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.REGISTRATION_CHECKOUT_STEP,
            state,
        ),
    );
    const {
        options: {
            sectionHeading = "Privacy Options",
            additionalText = "",
            displayPrivacyNameCheckboxLabel = "Don't show my name publicly",
            displayPrivacyAmountCheckboxLabel = "Don't show my gift amount publicly",
        },
    } = control;

    const teams = useSelector(selectTeams);
    const classifications = useSelector(selectAllClassifications);

    const registration = useSelector(selectAllRegistration);

    const {
        classification: { Classification = [] },
        registrations = [],
    } = registration;

    const {
        displayAnonymousDonorInformation = false,
        enableOptionalDonation,
        includePurchasesInGoal,
    } = useSelector(selectAllP2pSettings);

    const { optionalDonationAmount = 0.0, storeSubtotalAmount = 0.0 } =
        useSelector(selectRegistrationTotals);

    const isCms = useSelector(selectIsCms);

    const includePurchases = includePurchasesInGoal && storeSubtotalAmount > 0;
    const includeDonations =
        enableOptionalDonation && optionalDonationAmount > 0;

    const displayPrivacyOptions = includeDonations || includePurchases || isCms;

    // -------------------------------------------------------------------------
    // TODO: Hardcoded fields data for now. This is a placeholder for the actual
    //       PrivacyOptions fields data once they are moved into system fields
    //       for P2P Registration and the form is built out to use them.  This
    //       will be updated in the future and will work just like the OneForm™.
    // -------------------------------------------------------------------------
    const fields = [
        {
            id: 1,
            type: FieldType.SHOW_NAME,
            display_text: displayPrivacyNameCheckboxLabel,
        },
        {
            id: 2,
            type: FieldType.SHOW_AMOUNT,
            display_text: displayPrivacyAmountCheckboxLabel,
        },
    ];

    // -------------------------------------------------------------------------
    // NOTE: Primary Registrant is almost always the selected recipient of the optional
    // donation on P2P registrations. Since this is usually the person making
    // registering the callout is not necessary. The callout will only show for
    // NFP registrations where there is either a Classification or the Primary
    // Registrant has joined a team.
    // -------------------------------------------------------------------------
    const selectedRecipient = getSelectedRecipient({
        Classification,
        classifications,
        primaryRegistration: registrations[0] || {},
        teams,
    });

    const headingProps = {
        displayHeading: true,
        heading: sectionHeading,
        additionalText,
    };

    const inputProps = {
        ariaRequired: false,
        descText: "Optional",
        handleBlur,
        handleChange,
        headingProps,
        helpText: "",
        label: "",
        maxChar: undefined,
        errors,
        touched,
        setFieldValue,
        values,
    };

    const privacyOptionsProps = {
        settings: {
            displayAnonymousDonorInformation:
                !!displayAnonymousDonorInformation,
        },
        privacyFieldGroup: { fields },
        headingProps,
        selectedRecipient,
        values,
        ...inputProps,
    };

    return displayPrivacyOptions ? (
        <SystemFieldGroupPrivacyOptions {...privacyOptionsProps} />
    ) : null;
};

export default ConnectedSystemFieldGroupPrivacyOptions;
